import moment from "moment"
// import momentTimeZone from "moment-timezone"
import _ from "lodash"

import CONFIG from "configuration/environment"
import localizationHelpers from "services/localize/helpers"

// Configurable calendar and format date
// General full format date. It is used for booking date in confirmation page
export const formatFullDateDisplay = CONFIG.REACT_APP_DATE_FULL_FORMAT
  ? CONFIG.REACT_APP_DATE_FULL_FORMAT
  : "dddd Do MMMM YYYY, HH:mm:ss"
// Format date for penalties in hotel
export const penaltyFormatDate = CONFIG.REACT_APP_DATE_PENALTY_FORMAT
  ? CONFIG.REACT_APP_DATE_PENALTY_FORMAT
  : "MMMM Do YYYY, h:mm:ss a"
// Time format for hotel used for chechin and checkout times in confirmation page
export const hotelTimeFormat = CONFIG.REACT_APP_TIME_FORMAT
  ? CONFIG.REACT_APP_TIME_FORMAT
  : "hh:mm A"
// Format time for flight. (i.e: AM/PM format: "hh:mm A" o 24h format = "HH:mm"). It is used in flight search panel times dropdown list
export const flightTimeFormat = CONFIG.REACT_APP_TIME_FORMAT
  ? CONFIG.REACT_APP_TIME_FORMAT
  : "HH:mm"
// Format time for event. (i.e: AM/PM format: "hh:mm A" or 24h format = "HH:mm"). It is used in card on cart page
export const eventTimeFormat = CONFIG.REACT_APP_TIME_FORMAT ? CONFIG.REACT_APP_TIME_FORMAT : "HH:mm"

// Format Date used to save dates into state
export const standardFormatDateTime = "YYYY-MM-DDTHH:mm:ss"
export const standardFormatDate = "YYYY-MM-DD"
export const standardFormatTime = "HH:mm:ss"
// Format date for accessibility (how dates will be read)
export const formatDateAccessibility = "dddd Do MMMM YYYY"
export const formatShortFormDayDateAccessibility = "Do MMMM YYYY"
export const formatTimeAccessibility = CONFIG.REACT_APP_TIME_FORMAT
  ? CONFIG.REACT_APP_TIME_FORMAT
  : "hh:mm A"
export const formatDateTimeAccessibility = "dddd Do MMMM YYYY hh:mm A"

// Strip timezone offset suffix (e.g. "-07:00", "+05:30", "Z") from a datetime string
export const stripTimezoneOffset = value => {
  return typeof value === "string" ? value.replace(/[+-]\d{2}:?\d{2}$|Z$/, "") : value
}

/**
 * formats hours and minutes using, references flight so might be specific to that
 * @param  {string}   time          time or duration in format 07:54
 * @return {array}    [hrs, mins]   hrs and mins split and converted to int
 */
export const formatTimeToDuration = time => {
  // Split around the time colon
  let [hrs, mins] = time.split(":")

  // Parse to int
  hrs = parseInt(hrs, 10)
  mins = parseInt(mins, 10)

  return [hrs, mins]
}

export const getDifferenceNumberInHours = (fromValue, toValue) => {
  // Get the from and to string as a moment object
  const from = moment(stripTimezoneOffset(fromValue), standardFormatDateTime)
  const to = moment(stripTimezoneOffset(toValue), standardFormatDateTime)

  return from.diff(to, "hours")
}

export const getDurationInHours = (fromValue, toValue) => {
  const difference = getDifferenceNumberInHours(fromValue, toValue)

  return moment.duration(difference, "hours").humanize()
}

export const getDurationInDays = (fromValue, toValue, dateFormat = standardFormatDateTime) => {
  // Get the from and to string as a moment object
  const from = moment(stripTimezoneOffset(fromValue), dateFormat)
  const to = moment(stripTimezoneOffset(toValue), dateFormat)

  return Math.ceil(from.diff(to, "days", true))
}

export const isValid = (date, dateFormat = standardFormatDate) => {
  const newDate = moment(date, dateFormat, true)
  return newDate.isValid()
}

export const isInRange = (dateString, from, to) => {
  const dateFormat = localizationHelpers.getLocalizedCurrentDate()
  const date = getDateAsMoment(dateString, dateFormat)

  return date ? date.isBetween(from, to, "day", "[]") : false
}

// This function is to be used when we have a string date time
// and want to convert it into a moment object.
// (if no format is passed, it uses the standardFormatDate, which is the one used by the API
// but we can also pass another format - eg. to display dates according to the user locale format)
export const getDateAsMoment = (date, dateFormat = standardFormatDate) => {
  if (date) {
    const newDate = moment(stripTimezoneOffset(date), dateFormat, true)
    return newDate.isValid() ? newDate : null
  }
  return null
}

/**
 * This function is to be used when we have a string date time
 * and want to convert it into a moment object
 * using the users browser locale to format it
 *
 * @param {string} dateString
 */
export const formatDateWithUserLocale = dateString => {
  const dateFormat = localizationHelpers.getLocalizedCurrentDate()
  return getDateAsMoment(dateString, dateFormat)
}

// This function is to be used when we have a moment object
// and want to convert it into a string date.
// (if no format is passed, it uses the standardFormatDate, which is the one used by the API
// but we can also pass another format - eg. to display dates according to the user locale format)
export const getMomentAsDate = (date, dateFormat = standardFormatDate) => {
  if (date) {
    return date.format(dateFormat)
  }
  return ""
}

// This function is to be used when we have two string (date and time)
// and want to convert it into a moment object.
export const getDateTimeAsDate = (date, time) => {
  if (date && time) {
    const dateMoment = moment(`${date}T${time}`, "YYYY-MM-DDTHH:mm:ssZ")
    return dateMoment.format("YYYY-MM-DDTHH:mm:ssZ")
  }
  return null
}

// Return date formatted as formatTo. If formatTo is empty, date will be formatted based on locale
export const formatDateTime = (date, formatFrom = standardFormatDateTime, formatTo, showTime) => {
  if (_.isEmpty(date)) {
    return ""
  }
  if (_.isEmpty(formatTo)) {
    // if formatTo is not defined, date will be displayed based on locale format date
    const dateMoment = moment(date)
    if (dateMoment.isValid()) {
      return showTime
        ? dateMoment.format("llll")
        : dateMoment.format("llll").replace(dateMoment.format("LT"), "")
    }
    return ""
  }
  const dateMoment = moment(date, formatFrom)
  return dateMoment.isValid() ? dateMoment.format(formatTo) : ""
}

export const formatDate = (
  date,
  formatFrom = standardFormatDate,
  formatTo = localizationHelpers.getLocalizedCurrentDate()
) => {
  return formatDateTime(date, formatFrom, formatTo, false)
}

const formatHoursMinutes = value => {
  return value > 9 ? `${value}` : `0${value}`
}

// This function receives the day, month and year as numbers or strings,
// and returns the date as a string in standard format.
export const getDateFromFields = (day, month, year, dateFormat = standardFormatDate) => {
  const date = moment.utc([year, month - 1, day])

  return date.isValid() ? getMomentAsDate(date, dateFormat) : ""
}

// This function receives date and time as strings and the date format.
// It returns the date and time merged into one string in the standard format.
// date = "01/03/2019", time = "09:00", formatFrom = "DD/MM/YYYY" => "2019-03-01T09:00"
export const appendDateTime = (date, time, formatFrom) => {
  const formattedDate = formatDate(date, formatFrom, standardFormatDate)

  return `${formattedDate}T${time}`
}

export const initializedTimesList = (interval = 30, timeFormat = standardFormatTime) => {
  const numberOfMinutesInADay = 24 * 60
  const numIterations = numberOfMinutesInADay / interval
  return _.times(numIterations, interation => {
    const timeInMinutes = interval * interation
    const hour = _.floor(timeInMinutes / 60)
    const min = timeInMinutes % 60
    const value = `${formatHoursMinutes(hour)}:${formatHoursMinutes(min)}`
    return { value, label: formatDate(value, standardFormatTime, timeFormat) }
  })
}

export const getDateFormat = (showLongDateFormat, showTime) => {
  if (showTime) {
    return showLongDateFormat
      ? CONFIG.REACT_APP_DATE_TIME_LONG_FORMAT
      : CONFIG.REACT_APP_DATE_TIME_SHORT_FORMAT
  }
  return showLongDateFormat ? CONFIG.REACT_APP_DATE_LONG_FORMAT : CONFIG.REACT_APP_DATE_SHORT_FORMAT
}

export const formatAsStandardFormat = (dateString, defaultDate) => {
  const dateFormat = localizationHelpers.getLocalizedCurrentDate()
  return isValid(dateString) ? formatDate(dateString, dateFormat, standardFormatDate) : defaultDate
}

export const formatAsLocaleFormat = dateString => {
  const dateFormat = localizationHelpers.getLocalizedCurrentDate()
  return isValid(dateString) ? formatDate(dateString, standardFormatDate, dateFormat) : dateString
}

// [PLATDEV-48061] Same-day cancel restriction disabled — always returns false
// To re-enable, uncomment the original logic and remove `return false`
export const isSameAustraliaDate = (/* createdDateTime */) => {
  return false
  // return (
  //   createdDateTime &&
  //   momentTimeZone
  //     .utc(createdDateTime)
  //     .tz("Australia/Sydney")
  //     .isSame(momentTimeZone.tz("Australia/Sydney"), "day")
  // )
}
// Determine whether to show time based on the presence of time-related characters in the date string
export const shouldShowTime = date =>
  _.isString(date) && // Ensure `date` is a string
  (date.includes(" ") || // Check for space (e.g., "2025-01-01 14:30")
    date.includes("T") || // Check for ISO separator (e.g., "2025-01-01T14:30:00")
    /:\d{2}/.test(date)) // Check for time pattern like ":00", ":30", etc.
